import { type FC } from 'react'
import { type RegisterOptions, useFormContext } from 'react-hook-form'

import { FormControl, FormHelperText, FormLabel, Input } from 'base-ui'

import { type ContactFormValues } from '../../model/ContactFormValues'

type DirectContactFormTextFieldName = Exclude<
  keyof ContactFormValues,
  'fcSolution' | 'images' | 'subject'
>

type DirectContactFormTextFieldProps = {
  autoComplete?: string
  clearErrorOnClick?: boolean
  'data-qa-id': string
  disabled?: boolean
  label: string
  multiline?: boolean
  name: DirectContactFormTextFieldName
  required?: boolean
  rows?: number
  rules?: RegisterOptions<ContactFormValues, DirectContactFormTextFieldName>
  type?: 'email' | 'tel' | 'text'
}

const directContactFormTextFieldGetDisabledClassNames = ({
  disabled,
  multiline,
}: Pick<DirectContactFormTextFieldProps, 'disabled' | 'multiline'>): {
  input?: string
  root?: string
} => {
  if (!disabled) {
    return {}
  }

  if (multiline) {
    return {
      input: 'cursor-not-allowed',
      root: 'cursor-not-allowed',
    }
  }

  return {
    input: 'cursor-not-allowed text-dark-primary-strong',
    root: 'border-dark-primary-strong',
  }
}

export const DirectContactFormTextField: FC<
  DirectContactFormTextFieldProps
> = ({
  autoComplete,
  clearErrorOnClick,
  'data-qa-id': dataQaId,
  disabled,
  label,
  multiline,
  name,
  required,
  rows,
  rules,
  type = 'text',
}) => {
  const {
    clearErrors,
    formState: { errors },
    register,
    watch,
  } = useFormContext<ContactFormValues>()
  const errorMessage = errors[name]?.message
  const inputProps = register(name, rules)
  const handleClick: (() => void) | undefined = clearErrorOnClick
    ? (): void => {
        clearErrors(name)
      }
    : undefined
  const disabledClassNames = directContactFormTextFieldGetDisabledClassNames({
    disabled,
    multiline,
  })

  return (
    <FormControl
      className="w-full"
      disabled={disabled}
      error={Boolean(errorMessage)}
      required={required}
      value={watch(name)}
    >
      <FormLabel htmlFor={name}>{label}</FormLabel>
      {multiline ? (
        <Input
          autoComplete={autoComplete}
          className={disabledClassNames.root}
          id={name}
          multiline
          onClick={handleClick}
          rows={rows}
          slotProps={{
            input: {
              className: disabledClassNames.input,
              'data-qa-id': dataQaId,
            },
          }}
          {...inputProps}
        />
      ) : (
        <Input
          autoComplete={autoComplete}
          className={disabledClassNames.root}
          id={name}
          onClick={handleClick}
          slotProps={{
            input: {
              className: disabledClassNames.input,
              'data-qa-id': dataQaId,
            },
          }}
          type={type}
          {...inputProps}
        />
      )}
      {errorMessage ? (
        <FormHelperText data-qa-id={`${dataQaId}-error`}>
          {errorMessage}
        </FormHelperText>
      ) : null}
    </FormControl>
  )
}
